import { createElement } from 'react';
import { createTheme } from '@mui/material/styles';

const fontFamily = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`;

const slate = {
  50:  '#F8FAFC',
  100: '#F1F5F9',
  200: '#E2E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  800: '#1E293B',
  900: '#0F172A',
};

const brand = {
  primary: {
    main:         '#2563EB',
    light:        '#3B82F6',
    dark:         '#1D4ED8',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main:         '#2267CE',
    light:        '#4D89E0',
    dark:         '#1850A8',
    contrastText: '#FFFFFF',
  },
  success: { main: '#10B981', contrastText: '#FFFFFF' },
  warning: { main: '#F59E0B', contrastText: '#FFFFFF' },
  error:   { main: '#EF4444', contrastText: '#FFFFFF' },
  info:    { main: '#0EA5E9', contrastText: '#FFFFFF' },
};

// Deal-type colors. Keep these as the single source of truth and replace the
// hard-coded constants in DeskingContext/DealContext over time.
const deal = {
  lease: {
    light:        '#2B8EEA',
    main:         '#2267CE',
    dark:         '#1850A8',
    contrastText: '#FFFFFF',
  },
  purchase: {
    light:        '#1BA897',
    main:         '#0F938F',
    dark:         '#007D6E',
    contrastText: '#FFFFFF',
  },
};

// Every dropdown's chevron (the Quotes filters'): muted, blue once open.
export const ChevronDownIcon = ({ className }) => createElement('svg',
  { className, width: 10, height: 6, viewBox: '0 0 10 6', fill: 'none', 'aria-hidden': true },
  createElement('path', { d: 'M1 1L5 5L9 1', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' }));
const chevron = { color: 'var(--text-muted, #9ca3af)', transition: 'transform 0.18s' };
const chevronOpen = { color: 'var(--accent-blue, #2563eb)' };

export const baseTheme = createTheme({
  palette: {
    mode: 'light',
    primary:   brand.primary,
    secondary: brand.secondary,
    success:   brand.success,
    warning:   brand.warning,
    error:     brand.error,
    info:      brand.info,
    text: {
      primary:   slate[900],
      secondary: slate[600],
      disabled:  slate[400],
    },
    background: {
      default: '#F9F9F9',
      paper:   '#FFFFFF',
    },
    divider: slate[200],
    grey: { ...slate },
    slate,
    deal,
    // Dark-mode surface tokens (single source of truth for the new design).
    // Used today via CSS variables in the SCSS; future light/dark toggle
    // will swap these values from a ThemeContext.
    surface: {
      page:  '#0d1117',
      panel: '#161c2a',
      input: '#1e2435',
      hover: '#1e2435',
    },
    border: {
      default: '#252d3d',
    },
    accent: {
      blue:   '#3b82f6',
      green:  '#22c55e',
      purple: '#7b5ea7',
    },
  },

  shape: {
    borderRadius: 10,
  },

  typography: {
    fontFamily,
    h1:        { fontSize: '32px', fontWeight: 700, letterSpacing: '-0.02em' },
    h2:        { fontSize: '28px', fontWeight: 700, letterSpacing: '-0.01em' },
    h3:        { fontSize: '24px', fontWeight: 700, letterSpacing: '-0.01em' },
    h4:        { fontSize: '20px', fontWeight: 700 },
    h5:        { fontSize: '18px', fontWeight: 600 },
    h6:        { fontSize: '17px', fontWeight: 700, letterSpacing: '-0.01em' },
    subtitle1: { fontSize: '15px', fontWeight: 500 },
    subtitle2: { fontSize: '13px', fontWeight: 600 },
    body1:     { fontSize: '14px', fontWeight: 500 },
    body2:     { fontSize: '13px', fontWeight: 500 },
    button:    { fontSize: '14px', fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    caption:   { fontSize: '12px', fontWeight: 500, color: slate[600] },
    overline:  { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' },
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: slate[50],
          fontWeight: 600,
        },
        notchedOutline: {
          borderColor: slate[200],
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: 14,
          fontWeight: 600,
          color: slate[900],
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: ChevronDownIcon,
      },
      styleOverrides: {
        select: {
          fontSize: 14,
          fontWeight: 600,
        },
        icon: { ...chevron, top: 'calc(50% - 3px)', right: 12 },
        iconOpen: chevronOpen,
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: createElement(ChevronDownIcon),
      },
      styleOverrides: {
        popupIndicator: chevron,
        popupIndicatorOpen: chevronOpen,
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: slate[200],
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            borderRadius: 999,
          },
        },
      },
    },
  },
});
